import { useEffect, useRef, useState } from "react";
import {
  signInWithPassword,
  getUser,
  getAuthRememberMePreference,
  setAuthRememberMePreference,
  logAuthErrorDev,
  isInviteAcceptIntentNextPath,
} from "@tennis-matches/services/auth";
import {
  applyPendingSignupAccessRequestRemote,
  checkIsGlobalSuperadminRemote,
  REGISTRATION_ONBOARDING_OUTCOME,
  loadGovernancePendingWaitRemote,
  invalidateGovernanceBundleCache,
  checkSignupEmailAvailableRemote,
  isGlobalSuperadminForSession,
} from "@tennis-matches/services/governance";
import { navigateTo } from "@tennis-matches/core/router";
import { resolvePostLoginHomeRouteAsync } from "../utils/post-login-home.js";
import { resolvePostSignInNavigation } from "../utils/post-login-navigation.js";
import { readStoredPreferredHomeAppSync } from "../utils/home-routing.js";
import { useAuthEmailAvailabilityCheck } from "./useAuthEmailAvailabilityCheck.js";

function __getStoredInviteAcceptUrl() {
  try {
    const st = String(
      sessionStorage.getItem("tm_access_invite_token_v1") || "",
    ).trim();
    if (!st) return "";
    return `/accept-invite?token=${encodeURIComponent(st)}`;
  } catch {
    return "";
  }
}

function __isInvalidLoginCredentialsError(err) {
  const e = err && typeof err === "object" ? err : null;
  const msg = String(e?.message ?? err ?? "").toLowerCase();
  const code = String(e?.code ?? "").toLowerCase();
  return (
    code === "invalid_credentials" ||
    code === "invalid_login_credentials" ||
    msg.includes("invalid login credentials")
  );
}

function __markRegistrationOnboardingNotice(applied) {
  try {
    if (applied?.ok && applied?.outcome === REGISTRATION_ONBOARDING_OUTCOME) {
      sessionStorage.setItem("tm_registration_onboarding_notice", "1");
    }
  } catch (_) {}
}

function __markPostRegistrationAccessPending(applied) {
  try {
    if (applied?.ok && applied?.skipped !== true) {
      sessionStorage.setItem("tm_post_registration_access_pending", "1");
    }
  } catch (_) {}
}

function __clearPostRegistrationAccessPending() {
  try {
    sessionStorage.removeItem("tm_post_registration_access_pending");
  } catch (_) {}
}

/**
 * Email/password login fields, remember-me, auto-register notice, post-login nav.
 *
 * @param {object} options
 * @param {(key: string, params?: object) => string} options.t
 * @param {string} options.nextPath
 * @param {string} options.invitedEmail
 * @param {string} options.authMode
 * @param {(email: string) => void} options.onHandoffToRegister
 */
export function usePasswordLogin({
  t,
  nextPath,
  invitedEmail,
  authMode,
  onHandoffToRegister,
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() =>
    getAuthRememberMePreference(),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loginNotice, setLoginNotice] = useState("");
  const suppressAutoRegisterRef = useRef(false);

  const emailCheck = useAuthEmailAvailabilityCheck(email, {
    enabled: authMode === "login" && !invitedEmail,
  });

  useEffect(() => {
    if (authMode !== "login" || invitedEmail || suppressAutoRegisterRef.current)
      return;
    if (emailCheck.status === "taken") {
      setLoginNotice("already_registered");
      setInfo("");
      return;
    }
    if (emailCheck.status === "free") {
      const normalized = String(email || "")
        .trim()
        .toLowerCase();
      setLoginNotice("");
      if (typeof onHandoffToRegister === "function") {
        onHandoffToRegister(normalized);
      }
      return;
    }
    if (
      emailCheck.status === "idle" ||
      emailCheck.status === "checking" ||
      emailCheck.status === "error" ||
      emailCheck.status === "skipped" ||
      emailCheck.status === "rate_limited"
    ) {
      setLoginNotice("");
    }
  }, [authMode, invitedEmail, email, emailCheck.status, onHandoffToRegister]);

  const clearMessages = () => {
    setError("");
    setInfo("");
    setLoginNotice("");
  };

  const suppressAutoRegister = () => {
    suppressAutoRegisterRef.current = true;
  };

  const allowAutoRegister = () => {
    suppressAutoRegisterRef.current = false;
  };

  const onEmailChange = (value) => {
    setEmail(String(value || ""));
    setLoginNotice("");
    suppressAutoRegisterRef.current = false;
  };

  const afterPasswordSignInSuccess = async () => {
    const applied = await applyPendingSignupAccessRequestRemote();
    if (!applied?.ok && applied?.skipped !== true) {
      console.error(
        "[login] applyPendingSignupAccessRequestRemote failed",
        applied,
      );
      setError(t("auth.register.accessRequestPersistFailed"));
      return false;
    }
    __markRegistrationOnboardingNotice(applied);
    __markPostRegistrationAccessPending(applied);
    return true;
  };

  const onEmailPasswordLogin = async () => {
    clearMessages();
    setBusy(true);
    try {
      const normalizedEmail = String(email || "")
        .trim()
        .toLowerCase();
      const pw = String(password || "");
      if (!normalizedEmail || !pw) {
        setError(t("auth.validation.emailPasswordRequired"));
        return;
      }

      setAuthRememberMePreference(rememberMe);

      const emailAvailability =
        await checkSignupEmailAvailableRemote(normalizedEmail);
      if (emailAvailability?.ok && emailAvailability.available === true) {
        if (typeof onHandoffToRegister === "function") {
          onHandoffToRegister(normalizedEmail);
        }
        return;
      }

      const accountKnown =
        emailAvailability?.ok && emailAvailability.available === false;

      const { error: e } = await signInWithPassword({
        email: normalizedEmail,
        password: pw,
      });
      if (e) {
        logAuthErrorDev("login/password", e, { endpoint: "auth/v1/token" });
        setLoginNotice("");
        setError(
          __isInvalidLoginCredentialsError(e)
            ? accountKnown
              ? t("auth.login.errorWrongPassword")
              : t("auth.login.errorInvalidCredentials")
            : String(e.message || e),
        );
        return;
      }
      __clearPostRegistrationAccessPending();
      try {
        invalidateGovernanceBundleCache();
      } catch (_) {}
      const storedInviteUrl = __getStoredInviteAcceptUrl();
      const inviteIntent =
        isInviteAcceptIntentNextPath(nextPath) ||
        isInviteAcceptIntentNextPath(storedInviteUrl);
      if (!inviteIntent) {
        if (!(await afterPasswordSignInSuccess())) return;
      }
      const syncPreferred = readStoredPreferredHomeAppSync();
      const earlyDecision = resolvePostSignInNavigation({
        hasSession: true,
        nextPath,
        storedInviteUrl,
        nextPathMethod: "assign",
        applyPendingWaitGate: true,
        syncPreferred,
        asyncHomeRoute: "hub",
      });
      if (
        earlyDecision.reason === "next-path" ||
        earlyDecision.reason === "stored-invite"
      ) {
        window.location.assign(earlyDecision.target);
        return;
      }
      let isSuperadmin = false;
      try {
        const u = await getUser();
        isSuperadmin = isGlobalSuperadminForSession([], u);
        if (!isSuperadmin) {
          const rpcRes = await checkIsGlobalSuperadminRemote();
          isSuperadmin = Boolean(rpcRes?.ok && rpcRes?.isSuperadmin);
        }
      } catch (_) {
        isSuperadmin = false;
      }
      let pendingWait = null;
      if (!isSuperadmin) {
        pendingWait = await loadGovernancePendingWaitRemote();
      }
      const navCtx = {
        hasSession: true,
        nextPath,
        storedInviteUrl,
        nextPathMethod: "assign",
        applyPendingWaitGate: true,
        isSuperadmin,
        pendingWait,
        syncPreferred,
        asyncHomeRoute: "hub",
      };
      let decision = resolvePostSignInNavigation(navCtx);
      if (decision.reason === "async-home") {
        const homeRoute = await resolvePostLoginHomeRouteAsync();
        decision = resolvePostSignInNavigation({
          ...navCtx,
          asyncHomeRoute: homeRoute || "hub",
        });
      }
      if (decision.method === "assign") {
        window.location.assign(decision.target);
        return;
      }
      navigateTo(decision.target, true);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  return {
    email,
    setEmail,
    onEmailChange,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    rememberMe,
    setRememberMe,
    busy,
    error,
    info,
    setInfo,
    loginNotice,
    setLoginNotice,
    clearMessages,
    emailCheck,
    suppressAutoRegister,
    allowAutoRegister,
    onEmailPasswordLogin,
    getStoredInviteAcceptUrl: __getStoredInviteAcceptUrl,
  };
}
